import React, {useState} from 'react';
import {Drawer, Spin, Table} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type {ColumnsType} from 'antd/es/table';
import {
    GET_APPOINTMENT_INBOUND_PARCELS,
    GetAppointmentInboundParcelsResponse,
    GetAppointmentInboundParcelsVariables,
    InboundParcelItem
} from '../graphql/queries/getAppointmentInboundParcels';
import CentralParcelDetailDrawer from '../../../central/parcels/components/CentralParcelDetailDrawer';
import {useWarehouseContext} from '../../../../contexts/WarehouseContext';
import {formatEpochSecondsInHubZone} from '../../../../utils/timezoneUtils';
import {useTranslation} from 'react-i18next';
import {getUserInfo} from '../../../../utils/auth';

dayjs.extend(utc);
dayjs.extend(timezone);

interface InboundParcelsDrawerProps {
    visible: boolean;
    onClose: () => void;
    appointmentId: number;
    appointmentNumber?: string;
}

/**
 * 预约单「实际入库包裹」明细抽屉。
 *
 * 与 `PickupAppointmentParcelsDrawer`（预计揽收包裹）是**两个口径**的两个抽屉：
 * 那个是该单挂载的运单、可增删；本抽屉是该站点当日实际到仓扫描的运单、**只读**——
 * 入库归属由到仓扫描链路算出，运营手工增删没有业务含义。
 */
const PickupAppointmentInboundParcelsDrawer: React.FC<InboundParcelsDrawerProps> = ({
                                                                                        visible,
                                                                                        onClose,
                                                                                        appointmentId,
                                                                                        appointmentNumber
                                                                                    }) => {
    const {t} = useTranslation();
    const {currentWarehouse} = useWarehouseContext();
    const [parcelDetailVisible, setParcelDetailVisible] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<string>('');

    const {data, loading, error} = useQuery<GetAppointmentInboundParcelsResponse, GetAppointmentInboundParcelsVariables>(
        GET_APPOINTMENT_INBOUND_PARCELS,
        {
            variables: {id: appointmentId},
            skip: !visible || !appointmentId,
            fetchPolicy: 'network-only'
        }
    );

    const parcels = data?.getAppointmentInboundParcels ?? [];

    /**
     * 入库时间按 HUB 时区展示，与揽收进度页、揽收任务列表同口径。
     * 后端给的是 epoch 秒而非本地时间字符串——网关那一层拿不到该预约单的 HUB 时区。
     * 历史时刻按 IANA 时区换算（跨冬令时不差 1 小时），见 formatEpochSecondsInHubZone。
     */
    const formatArrivalScanAt = (epochSeconds: number | null): string =>
        formatEpochSecondsInHubZone(epochSeconds, currentWarehouse, '--');

    const columns: ColumnsType<InboundParcelItem> = [
        {
            title: t('pickup.appointment.inboundParcelPageDetails.no'),
            key: 'index',
            width: '10%',
            align: 'left',
            render: (_, __, index) => index + 1,
        },
        {
            title: t('pickup.appointment.inboundParcelPageDetails.parcel'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            width: '40%',
            align: 'left',
            render: (text: string | null, record) => (
                <a
                    className="text-purple-600 cursor-pointer hover:text-purple-800"
                    onClick={() => {
                        setSelectedOrderId(record.orderId);
                        setParcelDetailVisible(true);
                    }}
                >
                    {text || '--'}
                </a>
            ),
        },
        {
            title: t('pickup.appointment.inboundParcelPageDetails.status'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            width: '25%',
            align: 'left',
            render: (text: string | null) => <span className="text-purple-600">{text || '--'}</span>,
        },
        {
            title: t('pickup.appointment.inboundParcelPageDetails.arrivalScanAt'),
            dataIndex: 'arrivalScanAt',
            key: 'arrivalScanAt',
            width: '25%',
            align: 'left',
            render: (value: number | null) => (
                <span style={{whiteSpace: 'nowrap'}}>{formatArrivalScanAt(value)}</span>
            ),
        },
    ];

    return (
        <Drawer
            title={
                <div className="flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="h-6 w-1 bg-purple-600 mr-3"></div>
                        <span className="text-xl font-medium">
                            {t('pickup.appointment.inboundParcelPageDetails.title')}
                        </span>
                    </div>
                    <CloseOutlined onClick={onClose} className="cursor-pointer"/>
                </div>
            }
            width={800}
            placement="right"
            closable={false}
            onClose={onClose}
            open={visible}
        >
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large"/>
                </div>
            ) : error ? (
                <div className="text-red-500 text-center p-4">
                    Error loading parcels: {error.message}
                </div>
            ) : (
                <div className="flex flex-col h-full">
                    <div className="flex-1 space-y-4 overflow-y-auto">
                        <div className="grid grid-cols-2 gap-y-2 max-w-xs">
                            <div className="text-gray-500 text-left">
                                {t('pickup.appointment.inboundParcelPageDetails.appointmentId')}:
                            </div>
                            <div className="text-gray-700">{appointmentNumber}</div>

                            <div className="text-gray-500 text-left">
                                {t('pickup.appointment.inboundParcelPageDetails.parcelCount')}:
                            </div>
                            <div className="text-gray-700">{parcels.length}</div>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={parcels}
                            rowKey="orderId"
                            pagination={{pageSize: 10, showSizeChanger: false}}
                            size="small"
                        />
                    </div>
                </div>
            )}

            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                onClose={() => setParcelDetailVisible(false)}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
            />
        </Drawer>
    );
};

export default PickupAppointmentInboundParcelsDrawer;
